
import { useState, useCallback } from 'react';

/**
 * A hook that manages assessment responses in localStorage
 * @param assessmentId The ID of the assessment
 * @returns Functions and state for managing responses in localStorage
 */
export const useLocalStorageResponses = (assessmentId: string) => {
  // Generate storage key based on assessment ID
  const storageKey = `assessment_${assessmentId}_responses`;
  
  // Initialize state from localStorage
  const [responses, setResponsesState] = useState<Record<string, any>>(() => {
    try {
      const storedResponses = localStorage.getItem(storageKey);
      return storedResponses ? JSON.parse(storedResponses) : {};
    } catch (error) {
      console.error('Error loading responses from localStorage:', error);
      return {};
    }
  });
  
  // Update responses in state and localStorage
  const setResponses = useCallback((newResponses: any) => {
    try {
      // Support both direct value and function updater pattern
      const updatedResponses = typeof newResponses === 'function' 
        ? newResponses(responses) 
        : newResponses;
      
      // Update state
      setResponsesState(updatedResponses);
      
      // Persist to localStorage - do this in a separate callback to avoid triggering re-renders
      setTimeout(() => {
        try {
          localStorage.setItem(storageKey, JSON.stringify(updatedResponses));
        } catch (error) {
          console.error('Error saving responses to localStorage:', error);
        }
      }, 0);
    } catch (error) {
      console.error('Error updating responses:', error);
    }
  }, [responses, storageKey]);
  
  // Clear responses from state and localStorage
  const clearStoredResponses = useCallback(() => {
    setResponsesState({});
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error clearing responses from localStorage:', error);
    }
  }, [storageKey]);
  
  return {
    responses,
    setResponses,
    clearStoredResponses,
  };
};
